import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 border border-secondary-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300"
      >
        <div className="flex gap-4 p-4">
          <div className="w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              title={article.title}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
              {article.title}
            </h3>
            <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
              <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">{article.category}</span>
              <span className="whitespace-nowrap">{article.publishDate}</span>
              <span className="whitespace-nowrap">{article.viewCount.toLocaleString()} 阅读</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative bg-background-50 rounded-lg overflow-hidden border border-secondary-200/70 hover:border-primary-300/60 transition-all duration-300 h-full"
      >
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
            title={article.title}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent"></div>
          {article.isHot && (
            <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs px-3 py-1 rounded-full font-semibold whitespace-nowrap">
              热门
            </span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-background-50/80 bg-background-50/20 px-2 py-0.5 rounded-full whitespace-nowrap">{article.category}</span>
            <span className="text-xs text-background-50/70 whitespace-nowrap">{article.publishDate}</span>
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-background-50 line-clamp-2 leading-snug">
            {article.title}
          </h3>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-secondary-200/70 hover:border-primary-300/60 transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          title={article.title}
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">{article.category}</span>
          {article.isNew && (
            <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">NEW</span>
          )}
          {article.isHot && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">热</span>
          )}
        </div>
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">{article.summary}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-secondary-200/50">
          <div className="flex items-center gap-1 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.publishDate}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1"><i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i> {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount.toLocaleString()}</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line w-3 h-3 flex items-center justify-center"></i> {article.commentCount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}